import React, { useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { CheckIcon, PlusIcon } from "../icons";
import "./wizardQuill.css";
import Counter from "./Counter";

// Shared inputs for the Create League flow.
//
// Every length-limited input carries a live counter in its bottom-right corner
// and hard-stops at the limit (`maxLength`), so the count is a reassurance
// rather than an error waiting to happen.

// Only the three marks the toolbar offers — the emoji button inserts plain text,
// so it needs no format of its own. Restricting `formats` also strips pasted
// markup (headings, colours, links) that this field has no business carrying.
const QUILL_FORMATS = ["bold", "italic", "underline"];
// The toolbar lives in our own markup (see RichTextArea) and Quill binds to it
// by container, which is how the buttons keep the app's styling. Bound by id
// because Quill resolves the selector against the document when the editor
// mounts — the toolbar is rendered above it, so it's already there.
const TOOLBAR_ID = "wizard-quill-toolbar";
const QUILL_MODULES = { toolbar: { container: `#${TOOLBAR_ID}` } };

const htmlToText = (html) => {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n+$/, "");
};

// Quill never empties out — it leaves "<p><br></p>" behind. Treat that as blank
// so a wiped field fails the required check instead of passing with markup.
const isEmptyHtml = (html) => !htmlToText(html).trim();

// No asterisks anywhere in this flow: every field on the creation screens is
// mandatory, so marking them all would only add noise. validateStep enforces it.
//
// House rules for this flow's type: section headers are Montserrat near-black;
// the supporting line under one is Poppins, lighter and lighter-weight but not
// the placeholder grey; placeholders themselves are that lighter grey.
export const Field = ({ label, hint, error, children }) => (
  <div>
    {label && (
      <label className="block font-montserrat font-bold text-sm text-[#1A1A1A] mb-2">
        {label}
        {hint && (
          <span className="block mt-0.5 font-sans font-normal text-xs text-secondary">
            {hint}
          </span>
        )}
      </label>
    )}
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

// The white card every grouped block on these screens sits in: an icon tile, a
// Montserrat heading, an optional supporting line, and whatever the block holds.
// Lives here rather than in LeagueWizard so the standalone League settings screen
// renders the same card the wizard does.
export const InfoCard = ({ icon, title, description, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
    <div className="flex items-start gap-3">
      {icon && (
        <div className="shrink-0 w-12 h-12 rounded-xl bg-[#E6F2F0] flex items-center justify-center">
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h4 className="font-montserrat font-bold text-base text-[#0F172A]">{title}</h4>
        {description && <p className="mt-1 text-sm text-secondary">{description}</p>}
      </div>
    </div>
    {children && <div className="mt-3">{children}</div>}
  </div>
);

// Single-line input. `max` adds the counter and caps the value.
export const TextInput = ({ value, onChange, max, invalid, className = "", ...rest }) => (
  <div className="relative">
    <input
      {...rest}
      value={value}
      onChange={onChange}
      maxLength={max}
      className={`w-full rounded-xl border bg-white px-3.5 py-3 text-sm placeholder:text-tertiary focus:outline-none focus:border-[#0F4146] ${
        max ? "pb-6" : ""
      } ${invalid ? "border-red-400" : "border-[#E5E7EB]"} ${className}`}
    />
    {max ? <Counter value={value} max={max} /> : null}
  </div>
);

// Multi-line input. Plain textarea — used where the value is stored as plain
// text. The league Description uses RichTextArea below instead.
export const TextArea = ({ value, onChange, max, invalid, rows = 4, className = "", ...rest }) => (
  <div className="relative">
    <textarea
      {...rest}
      rows={rows}
      value={value}
      onChange={onChange}
      maxLength={max}
      className={`w-full rounded-xl border bg-white px-3.5 py-3 text-sm resize-none placeholder:text-tertiary focus:outline-none focus:border-[#0F4146] ${
        max ? "pb-7" : ""
      } ${invalid ? "border-red-400" : "border-[#E5E7EB]"} ${className}`}
    />
    {max ? <Counter value={value} max={max} /> : null}
  </div>
);

// A short, curated set — enough to give a league description some personality
// without pulling in a picker library for one button.
const EMOJI = ["🔥", "🚀", "📈", "💰", "🏆", "🥇", "🎯", "💎", "🐂", "🐻", "⚡", "🎓", "🤝", "😎", "👀", "💪"];

// Rich text, minimal toolbar: bold, italic, underline, emoji.
//
// Quill (already a dependency, and what the legacy create screen used) is what
// keeps this field producing the HTML the rest of the app renders through
// stripHtml. The limit counts VISIBLE characters, not markup, so bolding a word
// can't eat someone's description allowance — and it hard-stops, like every other
// capped field in the flow.
export const RichTextArea = ({ value, onChange, max, invalid, placeholder }) => {
  const quillRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Quill's own value is HTML; the counter and the cap work off the text.
  const plain = useMemo(() => htmlToText(value), [value]);

  const handleChange = (html, _delta, _source, editor) => {
    const text = editor.getText().replace(/\n+$/, "");
    // Past the cap, keep what we already had rather than truncating mid-markup.
    if (max && text.length > max && text.length > plain.length) return;
    onChange(isEmptyHtml(html) ? "" : html);
  };

  const insertEmoji = (emoji) => {
    setEmojiOpen(false);
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    if (max && plain.length + emoji.length > max) return;
    const at = editor.getSelection(true)?.index ?? editor.getLength();
    editor.insertText(at, emoji, "user");
    editor.setSelection(at + emoji.length, 0, "user");
  };

  return (
    <div className="relative">
      <div
        className={`rounded-xl border bg-white overflow-hidden ${
          invalid ? "border-red-400" : "border-[#E5E7EB]"
        }`}
      >
        <div
          id={TOOLBAR_ID}
          className="flex items-center gap-1 border-b border-divider px-2 py-1.5"
        >
          {/* Quill formats the selection through its own toolbar module, keyed by
              class name — hence the ql-* buttons rather than onClick handlers. */}
          <button type="button" className="ql-bold wizard-quill-btn" aria-label="Bold" />
          <button type="button" className="ql-italic wizard-quill-btn" aria-label="Italic" />
          <button type="button" className="ql-underline wizard-quill-btn" aria-label="Underline" />
          {/* The emoji button inserts text rather than formatting it, so it sits
              on the far side of a divider. */}
          <span aria-hidden="true" className="mx-1 h-4 w-px bg-divider" />
          <button
            type="button"
            onClick={() => setEmojiOpen((v) => !v)}
            aria-label="Insert emoji"
            aria-expanded={emojiOpen}
            className="wizard-quill-btn text-base leading-none"
          >
            🙂
          </button>
        </div>

        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={handleChange}
          placeholder={placeholder}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          className="wizard-quill"
        />
      </div>

      {emojiOpen && (
        <div className="absolute z-20 left-2 top-11 w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-lg grid grid-cols-8 gap-1">
          {EMOJI.map((e) => (
            <button
              type="button"
              key={e}
              onClick={() => insertEmoji(e)}
              className="h-7 rounded hover:bg-[#F3F4F6] text-base leading-none"
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {max ? <Counter value={plain} max={max} /> : null}
    </div>
  );
};

export const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    aria-label={label}
    className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${
      checked ? "bg-[#22A36B]" : "bg-[#D1D5DB]"
    }`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

// Icon picker: the four options in one row, per the design. Four across at every
// breakpoint (no wrapping, no sideways scroll), width-capped so the tiles stay
// tile-sized on a wide desktop card. The selected tile takes the green treatment
// — border, tick, and the glyph itself.
export const IconPicker = ({ options, value, onChange }) => (
  <div className="grid grid-cols-4 gap-3 max-w-sm">
    {options.map(({ key, label, Comp }) => {
      const selected = value === key;
      return (
        <button
          type="button"
          key={key}
          onClick={() => onChange(key)}
          aria-label={label}
          aria-pressed={selected}
          className={`relative aspect-square rounded-xl bg-white flex items-center justify-center transition-all ${
            selected
              ? "border-2 border-[#22A36B]"
              : "border border-[#E5E7EB] hover:border-[#0F4146]"
          }`}
        >
          <Comp className="w-7 h-7" stroke={selected ? "#22A36B" : "#0F4146"} />
          {selected && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#22A36B] text-white flex items-center justify-center">
              <CheckIcon className="w-3 h-3" />
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// Client Rev 3, private flow: "Increase Private League image upload limit from
// 5 MB to 15 MB. Image only, no video. If the existing image upload pipeline
// already resizes or compresses uploads, keep that behavior. Do not build a new
// image processing system specifically for this feature."
//
// Image-only was already true and is unchanged: `accept="image/*"` on the input
// and the `type.startsWith("image/")` check below, which is what actually
// enforces it — accept alone is only a file-picker filter. Nothing else about
// the pipeline is touched; the File still goes to the same uploader.
const MAX_IMAGE_MB = 15;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;

// League image upload — a private league's own art, which then represents it on
// its invite page and on every card that shows it.
//
// Click or drop. The File itself is what gets uploaded on submit; the object URL
// here is only the preview, and the caller revokes it when it's replaced, so
// picking three images in a row doesn't leak two of them.
export const ImageUpload = ({ file, preview, onSelect, onClear, invalid }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState("");

  const take = (candidate) => {
    if (!candidate) return;
    if (!candidate.type?.startsWith("image/")) {
      setProblem("That file isn't an image.");
      return;
    }
    if (candidate.size > MAX_IMAGE_BYTES) {
      setProblem(`Keep the image under ${MAX_IMAGE_MB} MB.`);
      return;
    }
    setProblem("");
    onSelect(candidate);
  };

  const hidden = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={(e) => take(e.target.files?.[0])}
    />
  );

  if (file && preview) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-white p-3">
        <img src={preview} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#F3F4F6]" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#0F172A] truncate">{file.name}</p>
          <p className="mt-0.5 text-xs text-tertiary">{(file.size / 1024).toFixed(0)} KB</p>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="shrink-0 text-sm font-semibold text-[#0F4146] hover:underline"
        >
          Replace
        </button>
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 text-sm font-medium text-secondary hover:text-red-500"
        >
          Remove
        </button>
        {hidden}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          take(e.dataTransfer.files?.[0]);
        }}
        aria-label="Upload a league image"
        className={`w-full rounded-xl border border-dashed px-4 py-4 flex items-center justify-center transition-colors ${
          dragging
            ? "border-[#22A36B] bg-[#F0FBF5]"
            : invalid || problem
            ? "border-red-300 bg-white"
            : "border-[#D1D5DB] bg-white hover:border-[#0F4146]"
        }`}
      >
        <PlusIcon className="w-5 h-5" />
        {hidden}
      </button>
      {problem ? (
        <p className="mt-1.5 text-xs text-red-500">{problem}</p>
      ) : (
        <p className="mt-1.5 text-xs text-secondary">
          By uploading, you agree this image meets QUSHi's standards.
        </p>
      )}
    </div>
  );
};

// Prefix + slug input for the league link. The prefix differs per flow
// (leagues/ vs join/) and is supplied by the variant config.
export const LinkInput = ({ prefix, value, onChange, invalid }) => (
  <div
    className={`flex rounded-xl border overflow-hidden bg-white ${
      invalid ? "border-red-400" : "border-[#E5E7EB]"
    }`}
  >
    <span className="bg-[#F3F4F6] px-3 py-3 text-sm font-medium text-[#0F172A] whitespace-nowrap">
      {prefix}
    </span>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder="your-league-name"
      className="flex-1 px-3 py-3 text-sm placeholder:text-tertiary focus:outline-none min-w-0"
    />
  </div>
);
